import React, { PropsWithoutRef } from 'react';

import { Box } from '@tanium/react-box';
import styled from '@tanium/react-emotion-9';
import { useUsingMouse } from '@tanium/react-mouse-interaction-context';
import { css } from '@tanium/style-system';
import { ThemeSpace } from '@tanium/theme-data';

import {
  ActiveBorder,
  ActiveFill,
  Border,
  DisabledBorder,
  DisabledDotFill,
  DisabledFill,
  DisabledSelectedFill,
  Fill,
  FocusShadow,
  HoverBorder,
  InvalidBorder,
  SelectedFill,
} from './themeDefinitions';

export interface Props extends Omit<PropsWithoutRef<JSX.IntrinsicElements['input']>, 'css'> {
  /**
   * Whether the radio button should be displayed in an invalid (error) state.
   */
  invalid?: boolean;
}

/**
 * Width and height measurement of the radio button (in pixels).
 */
export const RADIOBUTTON_SIZE = 16;

/**
 * Padding on each side of the dot within the radio button (as a theme-space value).
 */
const RADIOBUTTON_PADDING = ThemeSpace.minor[1];

/**
 * This span wraps around everything in this component and ensures that this component flows
 * properly in the DOM.
 */
const Wrapper = styled('span')({
  position: 'relative',
  display: 'inline-block',
  width: RADIOBUTTON_SIZE,
  height: RADIOBUTTON_SIZE,
});

/**
 * This span wraps just the fake radio button portion of the component; it is what the user sees and
 * it does not include the hidden input element. The user clicks right through this element onto the
 * invisible input element below.
 */
const FakeRadioWrapper = styled('span')({
  pointerEvents: 'none',
});

const FakeRadioCircle = styled(Box)({});

const FakeRadioDot = styled(Box)({
  visibility: 'hidden',
});

const BOX_SELECTOR = `+ ${FakeRadioWrapper} > ${FakeRadioCircle}`;
const DOT_SELECTOR = `+ ${FakeRadioWrapper} > ${FakeRadioDot}`;

/*
 * The radio input element is completely transparent and placed behind the custom styled fake radio
 * button.
 */
const HiddenInput = styled('input', {
  shouldForwardProp: (p) => ['innerRef', 'invalid', 'usingMouse'].indexOf(p) === -1,
})<{ invalid: boolean; usingMouse: boolean }>(
  {
    left: 0,
    opacity: 0,
    position: 'absolute',
    top: 0,
    width: RADIOBUTTON_SIZE,
    height: RADIOBUTTON_SIZE,
    // override top margin on input[type="radio"] from bootstrap
    '&&': { margin: 0 },
  },
  // attribute selectors are necessary to target FakeRadioWrapper, which suppresses pointer events
  {
    ':disabled': {
      [BOX_SELECTOR]: {
        cursor: 'not-allowed',
      },
    },
    ':checked': {
      [DOT_SELECTOR]: {
        visibility: 'visible',
      },
    },
  },
  ({ invalid, usingMouse }) => [
    css({
      ':disabled': {
        [BOX_SELECTOR]: {
          backgroundColor: DisabledFill,
        },
        [DOT_SELECTOR]: {
          backgroundColor: DisabledDotFill,
        },
        ':checked': {
          [BOX_SELECTOR]: {
            backgroundColor: DisabledSelectedFill,
          },
        },
      },
    }),
    !invalid &&
      css({
        ':disabled': {
          [BOX_SELECTOR]: {
            borderColor: DisabledBorder,
          },
        },
        ':not(:disabled):hover': {
          [BOX_SELECTOR]: {
            borderColor: HoverBorder,
          },
        },
      } as {}),
    css({
      [usingMouse ? ':active' : ':active:focus']: {
        [BOX_SELECTOR]: {
          ...(!invalid && { borderColor: ActiveBorder }),
          bg: ActiveFill,
        },
      },
    }),
    !usingMouse &&
      css({
        ':focus': {
          [BOX_SELECTOR]: {
            boxShadow: FocusShadow,
          },
        },
      }),
  ],
);

/**
 * A radio button component.
 */
const Radio = React.forwardRef<HTMLInputElement, Props>(({ invalid, ...props }, ref) => {
  const usingMouse = useUsingMouse();

  return (
    <Wrapper>
      <HiddenInput
        {...props}
        innerRef={ref}
        type="radio"
        invalid={!!invalid}
        usingMouse={usingMouse}
      />
      <FakeRadioWrapper aria-hidden="true">
        <FakeRadioCircle
          position="absolute"
          width={2}
          height={2}
          border={1}
          bg={Fill}
          borderColor={invalid ? InvalidBorder : Border}
          borderRadius="50%"
        />
        <FakeRadioDot
          bg={SelectedFill}
          position="absolute"
          width={1}
          height={1}
          top={RADIOBUTTON_PADDING}
          left={RADIOBUTTON_PADDING}
          borderRadius="50%"
        />
      </FakeRadioWrapper>
    </Wrapper>
  );
});

export default Radio;
